// ============================================================================
// Revenue metrics — pure functions over normalized AnalysisStay records.
//
// Two revenue attribution modes:
//   'arrival'    — the full stay cost lands on the check-in date's bucket
//   'stay-night' — cost/days is spread across nights, clipped to the window
// ============================================================================

import { daysBetween } from '../dateUtils';
import { bucketKey, bucketLabel, enumerateBuckets, groupBy, median, sumBy } from './aggregations';
import { AnalysisStay, Granularity, RevenuePoint, isRevenueStay } from './types';

export type RevenueMode = 'arrival' | 'stay-night';

/** Nights of a stay that fall inside [from, to] (checkOut is exclusive). */
export function clippedNights(stay: AnalysisStay, from: string, to: string): number {
  const start = stay.checkIn > from ? stay.checkIn : from;
  // checkOut is exclusive; window `to` is the last included day
  const endExclusive = stay.checkOut <= to ? stay.checkOut : undefined;
  const nights = endExclusive !== undefined
    ? daysBetween(start, endExclusive)
    : daysBetween(start, to) + 1;
  return Math.max(0, nights);
}

/** Active-site count for occupancy weighting (min 1 for siteless rows). */
function activeSiteCount(stay: AnalysisStay): number {
  const active = stay.sites.filter(s => s.status === 'active').length;
  return Math.max(1, active);
}

/** Occupied site-nights of revenue stays clipped to [from, to]. */
export function occupiedSiteNights(stays: AnalysisStay[], from: string, to: string): number {
  return sumBy(
    stays.filter(isRevenueStay),
    stay => clippedNights(stay, from, to) * activeSiteCount(stay)
  );
}

/** Revenue clipped to [from, to] under the given attribution mode. */
export function revenueInWindow(
  stays: AnalysisStay[],
  from: string,
  to: string,
  mode: RevenueMode = 'arrival'
): number {
  let total = 0;
  for (const stay of stays) {
    if (!isRevenueStay(stay)) continue;
    if (mode === 'arrival') {
      if (stay.checkIn >= from && stay.checkIn <= to) total += stay.cost;
    } else {
      if (stay.days > 0) total += (stay.cost / stay.days) * clippedNights(stay, from, to);
    }
  }
  return total;
}

/** Revenue time series bucketed at the given granularity. */
export function revenueSeries(
  stays: AnalysisStay[],
  from: string,
  to: string,
  granularity: Granularity,
  mode: RevenueMode = 'arrival'
): RevenuePoint[] {
  const buckets = enumerateBuckets(from, to, granularity);
  const revenue = new Map<string, number>(buckets.map(b => [b, 0]));
  const bookings = new Map<string, number>(buckets.map(b => [b, 0]));

  for (const stay of stays) {
    if (!isRevenueStay(stay)) continue;
    if (mode === 'arrival') {
      if (stay.checkIn < from || stay.checkIn > to) continue;
      const key = bucketKey(stay.checkIn, granularity);
      if (revenue.has(key)) {
        revenue.set(key, revenue.get(key)! + stay.cost);
        bookings.set(key, bookings.get(key)! + 1);
      }
    } else {
      if (stay.days <= 0) continue;
      const perNight = stay.cost / stay.days;
      let counted = false;
      for (let night = stay.checkIn; night < stay.checkOut; ) {
        if (night >= from && night <= to) {
          const key = bucketKey(night, granularity);
          if (revenue.has(key)) {
            revenue.set(key, revenue.get(key)! + perNight);
            if (!counted) {
              bookings.set(key, bookings.get(key)! + 1);
              counted = true;
            }
          }
        }
        // advance one day without re-parsing the whole stay
        const d = new Date(night + 'T12:00:00');
        d.setDate(d.getDate() + 1);
        night = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
    }
  }

  return buckets.map(key => ({
    bucket: key,
    label: bucketLabel(key, granularity),
    value: Math.round(revenue.get(key)! * 100) / 100,
    bookings: bookings.get(key)!,
  }));
}

/** Average daily rate: revenue per occupied site-night in the window. */
export function computeAdr(stays: AnalysisStay[], from: string, to: string): number | null {
  const nights = occupiedSiteNights(stays, from, to);
  if (nights === 0) return null;
  return revenueInWindow(stays, from, to, 'stay-night') / nights;
}

/**
 * Revenue per available site-night. `availableSiteNights` =
 * siteCount × nights in window − blacked-out site-nights.
 */
export function computeRevPas(
  stays: AnalysisStay[],
  from: string,
  to: string,
  availableSiteNights: number
): number | null {
  if (availableSiteNights <= 0) return null;
  return revenueInWindow(stays, from, to, 'stay-night') / availableSiteNights;
}

// ============================================================================
// Booking lead time
// ============================================================================

export interface LeadTimeBucket {
  label: string;
  minDays: number;
  bookings: number;
  revenue: number;
}

const LEAD_TIME_BUCKETS: Array<{ label: string; min: number; max: number }> = [
  { label: '0–7 days', min: 0, max: 7 },
  { label: '8–30 days', min: 8, max: 30 },
  { label: '31–90 days', min: 31, max: 90 },
  { label: '90+ days', min: 91, max: Infinity },
];

export function leadTimeDays(stay: AnalysisStay): number | null {
  if (!stay.createdAt) return null;
  const created = stay.createdAt.split('T')[0];
  return daysBetween(created, stay.checkIn);
}

export function leadTimeBuckets(stays: AnalysisStay[], from: string, to: string): LeadTimeBucket[] {
  const buckets = LEAD_TIME_BUCKETS.map(b => ({
    label: b.label,
    minDays: b.min,
    bookings: 0,
    revenue: 0,
  }));
  for (const stay of stays) {
    if (!isRevenueStay(stay)) continue;
    if (stay.checkIn < from || stay.checkIn > to) continue;
    const lead = leadTimeDays(stay);
    if (lead === null || lead < 0) continue;
    const idx = LEAD_TIME_BUCKETS.findIndex(b => lead >= b.min && lead <= b.max);
    if (idx >= 0) {
      buckets[idx].bookings += 1;
      buckets[idx].revenue += stay.cost;
    }
  }
  return buckets;
}

export function medianLeadTime(stays: AnalysisStay[], from: string, to: string): number | null {
  const leads = stays
    .filter(isRevenueStay)
    .filter(s => s.checkIn >= from && s.checkIn <= to)
    .map(leadTimeDays)
    .filter((d): d is number => d !== null && d >= 0);
  if (leads.length === 0) return null;
  return median(leads);
}

// ============================================================================
// Revenue by site type
// ============================================================================

export interface SiteTypeRevenue {
  typeName: string;
  revenue: number;
}

/**
 * Sum per-site revenue (reservation_sites.site_cost, never the whole
 * reservation cost) grouped by site type. Stays are windowed by arrival.
 */
export function revenueBySiteType(
  stays: AnalysisStay[],
  from: string,
  to: string,
  typeIdBySiteId: Map<number, number>,
  typeNameById: Map<number, string>
): SiteTypeRevenue[] {
  const rows: Array<{ typeName: string; cost: number }> = [];
  for (const stay of stays) {
    if (!isRevenueStay(stay)) continue;
    if (stay.checkIn < from || stay.checkIn > to) continue;
    for (const site of stay.sites) {
      if (site.status !== 'active') continue;
      const typeId = typeIdBySiteId.get(site.siteId);
      const typeName = (typeId !== undefined && typeNameById.get(typeId)) || 'Other';
      rows.push({ typeName, cost: site.siteCost });
    }
  }
  const grouped = groupBy(rows, r => r.typeName);
  return Array.from(grouped.entries())
    .map(([typeName, items]) => ({ typeName, revenue: sumBy(items, i => i.cost) }))
    .sort((a, b) => b.revenue - a.revenue);
}
